import { useCallback, useEffect, useState } from 'react';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { trackTimeSpent } from '../services/AnalyticsService';
import GiftSetService from '../services/GiftSetService';
import { DEFAULT_TEMPLATES } from '../services/templates';
import {
  DEFAULT_STORY_COACH_ANSWERS,
  getGiftFormatForOpeningMode,
  normalizeSharingSettings,
} from '../services/giftBuilderConfig';

const getPageIdFromPath = () => {
  const segments = window.location.pathname.split('/').filter(Boolean);
  const pageSegmentIndex = segments.findIndex((segment) => segment === 'page' || segment === 'pages');
  return pageSegmentIndex >= 0 ? segments[pageSegmentIndex + 1] : segments[0] || 'demo';
};

const getLocalPreviewTemplate = () => {
  const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  if (!isLocalHost) return null;
  const previewKey = new URLSearchParams(window.location.search).get('localPreview');
  return previewKey && DEFAULT_TEMPLATES[previewKey] ? DEFAULT_TEMPLATES[previewKey] : null;
};

const DEFAULT_TEMPLATE_KEY = 'mom';
const DATA_SCHEMA_VERSION = 2;

const cloneTemplateModule = (module) => JSON.parse(JSON.stringify(module));

export const createTemplateModules = (templateKey = DEFAULT_TEMPLATE_KEY, idPrefix = 'm') => {
  const template = DEFAULT_TEMPLATES[templateKey] || DEFAULT_TEMPLATES[DEFAULT_TEMPLATE_KEY];
  const stamp = Date.now();

  return (template?.modules || [])
    .filter((module) => module.type !== 'product_identity')
    .map((module, index) => ({
      ...cloneTemplateModule(module),
      id: `${idPrefix}_${stamp}_${index}`,
    }));
};

const createTemplatePageData = (templateKey = DEFAULT_TEMPLATE_KEY, idPrefix = 'm') => {
  const template = DEFAULT_TEMPLATES[templateKey] || DEFAULT_TEMPLATES[DEFAULT_TEMPLATE_KEY];
  const templateData = template?.data || {};

  return {
    ...templateData,
    templateKey,
    schemaVersion: DATA_SCHEMA_VERSION,
    ownerNameTouched: false,
    openingTitleTouched: false,
    openingSubtitleTouched: false,
    giftFormat: templateData.giftFormat || 'layer_by_layer',
    openingMode: templateData.openingMode || 'layered_gift',
    openingStyleTouched: false,
    sharingSettings: normalizeSharingSettings(templateData.sharingSettings),
    storyCoachAnswers: {
      ...DEFAULT_STORY_COACH_ANSWERS,
      ...(templateData.storyCoachAnswers || {}),
    },
    modules: createTemplateModules(templateKey, idPrefix),
  };
};

const createDefaultPageData = () => createTemplatePageData(DEFAULT_TEMPLATE_KEY, 'default');

const getTemplateKey = (data = {}) => data.templateKey || data.presetKey || data.preset || data.template;

const hasTouchedIntroField = (data = {}, field) => {
  if (data[`${field}Touched`] !== undefined) {
    return Boolean(data[`${field}Touched`]);
  }

  const value = String(data[field] || '').trim();
  if (!value) return false;

  const templateKey = getTemplateKey(data);
  const templateDefault = templateKey ? DEFAULT_TEMPLATES[templateKey]?.data?.[field] : '';
  return value !== String(templateDefault || '').trim();
};

const normalizePageData = (data = {}) => {
  const templateKey = getTemplateKey(data) || DEFAULT_TEMPLATE_KEY;
  const rawModules = Array.isArray(data.modules) ? data.modules : [];
  const modules = rawModules.length > 0
    ? rawModules.map((module, index) => ({ ...module, id: module.id || `m_${index}` }))
    : createTemplateModules(templateKey, 'restored');

  return {
    ...data,
    templateKey,
    ownerName: data.ownerName || '',
    ownerNameTouched: hasTouchedIntroField(data, 'ownerName'),
    openingTitle: data.openingTitle || '',
    openingTitleTouched: hasTouchedIntroField(data, 'openingTitle'),
    openingSubtitle: data.openingSubtitle || '',
    openingSubtitleTouched: hasTouchedIntroField(data, 'openingSubtitle'),
    schemaVersion: Number(data.schemaVersion || 1),
    giftFormat: data.giftFormat || getGiftFormatForOpeningMode(data.openingMode),
    openingMode: data.openingMode || 'layered_gift',
    openingStyleTouched: Boolean(data.openingStyleTouched),
    themeId: data.themeId || 'soft_serenity',
    sharingSettings: normalizeSharingSettings(data.sharingSettings),
    storyCoachAnswers: {
      ...DEFAULT_STORY_COACH_ANSWERS,
      ...(data.storyCoachAnswers || {}),
    },
    modules,
  };
};

const getContentVersion = (data = {}) => (
  data.updatedAt?.seconds ||
  data.updatedAt?._seconds ||
  data.updatedAt ||
  data.createdAt?.seconds ||
  data.createdAt?._seconds ||
  'v1'
);

export const useGiftPage = ({ setCurrentThemeId }) => {
  const [params, setParams] = useState({ nfcId: null });
  const [pageData, setPageData] = useState(createDefaultPageData);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState('');
  const [isLocked, setIsLocked] = useState(false);
  const [hasSeenOpening, setHasSeenOpening] = useState(false);
  const [giftSetContext, setGiftSetContext] = useState(null);

  useEffect(() => {
    const nfcId = getPageIdFromPath();
    setParams({ nfcId });

    const fetchData = async () => {
      try {
        const localPreviewTemplate = getLocalPreviewTemplate();
        if (localPreviewTemplate) {
          const previewData = normalizePageData(localPreviewTemplate.data);
          setPageData({
            ...previewData,
            modules: localPreviewTemplate.modules
              .filter((module) => module.type !== 'product_identity')
              .map((module, index) => ({ ...module, id: `preview_${index}` })),
          });
          if (previewData.themeId) setCurrentThemeId(previewData.themeId);
          setIsLocked(false);
          setHasSeenOpening(true);
          setGiftSetContext(null);
          setIsLoading(false);
          return;
        }

        const result = await GiftSetService.resolveGiftPage(nfcId);
        if (result?.success && result.pageData) {
          const data = result.pageData;
          setPageData(normalizePageData(data));
          if (data.themeId) setCurrentThemeId(data.themeId);
          setGiftSetContext({
            requestedUuid: result.requestedUuid || nfcId,
            contentPageId: result.contentPageId || nfcId,
            isLinkedMember: Boolean(result.isLinkedMember),
            device: result.device || null,
            giftGroup: result.giftGroup || null,
          });

          const locked = data.isLocked === true;
          setIsLocked(locked);
          const contentVersion = getContentVersion(data);
          const openedKey = `lumirae_opened_${nfcId}_${contentVersion}`;
          const openedBefore = localStorage.getItem(openedKey) === '1';
          setHasSeenOpening(openedBefore);
          if (locked && !openedBefore) localStorage.setItem(openedKey, '1');

          if (!auth.currentUser) {
            await signInAnonymously(auth);
          }
        } else {
          console.warn('Page not found');
          setPageError('Page not found. Please check your NFC/QR link.');
          setGiftSetContext(null);
          if (!auth.currentUser) await signInAnonymously(auth);
        }
      } catch (error) {
        console.error('Sync Error:', error);
        const pendingBind = GiftSetService.getPendingBind();
        if (pendingBind && pendingBind.ownerUuid !== nfcId) {
          setPageData(createDefaultPageData());
          setGiftSetContext({
            requestedUuid: nfcId,
            contentPageId: nfcId,
            isLinkedMember: false,
            device: null,
            giftGroup: null,
            isBindCandidate: true,
          });
          setIsLocked(false);
          setHasSeenOpening(true);
          setPageError('');
          if (!auth.currentUser) await signInAnonymously(auth);
          setIsLoading(false);
          return;
        }
        setGiftSetContext(null);
        setPageError(error.message || 'Page not found. Please check your NFC/QR link.');
      }
      setIsLoading(false);
    };

    fetchData();

    const handleUnload = () => trackTimeSpent(nfcId);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
  }, [setCurrentThemeId]);

  const applyTemplate = useCallback((templateKey) => {
    const template = DEFAULT_TEMPLATES[templateKey];
    if (!template) return;

    setPageData(createTemplatePageData(templateKey, 'm'));
    if (template.data.themeId) setCurrentThemeId(template.data.themeId);
  }, [setCurrentThemeId]);

  const resetGiftPage = useCallback(() => {
    setPageData(createDefaultPageData());
    setIsLocked(false);
    setHasSeenOpening(false);
    setPageError('');
    setGiftSetContext(null);
    setCurrentThemeId(DEFAULT_TEMPLATES[DEFAULT_TEMPLATE_KEY]?.data?.themeId || 'pearl_rose');
  }, [setCurrentThemeId]);

  return {
    params,
    pageData,
    setPageData,
    isLoading,
    setIsLoading,
    pageError,
    isLocked,
    setIsLocked,
    hasSeenOpening,
    setHasSeenOpening,
    giftSetContext,
    applyTemplate,
    resetGiftPage,
  };
};

export default useGiftPage;
